import { z } from 'zod';
import { OUTBOUND_CALL_OUTCOME_VALUES } from '../constants/outbound-call-outcome';
import { audienceParamFields, defaultAudience } from '../schemas/outreach-audience-fields';
import {
  AUDIENCE_CONDITION_TYPES,
  AUDIENCE_FILTER_FIELDS,
  WINDOW_DAYS,
  audienceSchema,
  campaignSpecSchema,
  timeOfDayString,
  type Audience,
  type AudienceFilterField,
  type CampaignSpec,
  type SequenceStep,
} from '../schemas/outreach-campaign';

/**
 * The part of a campaign a sentence can express, and how it merges back.
 *
 * This is the compiler's output shape — **not** a campaign spec. The
 * distinction is the whole safety property of the feature: the English
 * description is a *lens* over the spec, not a serialization of it, and a spec
 * carries settings no sentence mentions (a voicemail's message slot, a step's
 * own narrowed calling window, the SMS quiet-period that resolves it). A
 * compiler that emitted a whole spec would drop every one of them on each round
 * trip, silently, and the author would have no way to notice.
 *
 * So the model emits only what the prose can say, and {@link applyCampaignEdit}
 * merges that onto the campaign already in hand. Anything the model did not
 * mention is not "reset to default" — it is untouched, because it was never
 * part of the conversation.
 *
 * **Every field is nullable rather than optional.** `null` means "the text did
 * not say", which is the same instruction as "leave it alone". Structured
 * output is markedly more reliable when the shape is fixed and absence is a
 * value than when keys may or may not appear.
 */

/**
 * One audience parameter. Split by type rather than a union, because a JSON
 * schema union of primitives is the shape structured output most often gets
 * wrong — and a `noVisitForMonths` that arrives as `true` is a campaign
 * pointed at the wrong population.
 */
export const campaignEditParamSchema = z.object({
  key: z.string(),
  numberValue: z.number().nullable(),
  booleanValue: z.boolean().nullable(),
});

export const campaignEditFilterSchema = z.object({
  field: z.enum(AUDIENCE_FILTER_FIELDS),
  /**
   * Display names, never ids. The model is shown the locations and providers
   * an organization has and picks among them by name; resolving those back to
   * uuids is this module's job. A model asked for a uuid invents one.
   */
  names: z.array(z.string()),
});

export const campaignEditStepSchema = z.object({
  /**
   * The id of the step this entry replaces, or `null` for a new one.
   *
   * This is what carries per-step settings across a round trip. An entry with
   * an id keeps that step's window, message slot and resolve rule; an entry
   * without one is genuinely new and gets defaults.
   */
  id: z.string().nullable(),
  label: z.string(),
  channel: z.enum(['voice', 'sms']),
  times: z.number().int().min(1).max(10),
  spacingMinutes: z.number().int().min(0),
  /** Ignored on the first step, which always starts on qualification. */
  waitBeforeMinutes: z.number().int().min(0),
  continueOn: z.array(z.string()),
  /*
    Nullable, unlike everything else on a step, because these three are
    INVISIBLE IN THE PROSE WHEN FALSE. A step that does not rotate its time of
    day, or does not honour a requested callback, produces no sentence at all —
    absence of a phrase is the only signal. So a model restating the sequence
    (which it must, since the sequence is all-or-nothing) has no way to say
    "unchanged" and is forced to guess.

    Observed, not theorised: asked only to change a step's call count, the model
    also switched `honourRequestedCallback` on for both steps. `null` gives it
    somewhere to put "the text did not say", and {@link applyStep} reads that as
    the value the step already had.
  */
  leaveMessage: z.boolean().nullable(),
  rotateTimeOfDay: z.boolean().nullable(),
  honourRequestedCallback: z.boolean().nullable(),
});

/** Only the success shapes a sentence can state. Anything else is left alone. */
export const CAMPAIGN_EDIT_SUCCESS_KINDS = [
  'appointment_booked',
  'appointment_confirmed',
  'outcome',
] as const;

export const campaignEditSchema = z.object({
  audience: z
    .object({
      type: z.enum(AUDIENCE_CONDITION_TYPES).nullable(),
      params: z.array(campaignEditParamSchema).nullable(),
      filters: z.array(campaignEditFilterSchema).nullable(),
    })
    .nullable(),
  /**
   * The whole sequence, or null to leave it alone.
   *
   * Whole rather than per-step patches: the sequence paragraph is a complete
   * statement of the sequence, so a step the author deleted a sentence for is a
   * step they meant to remove. Partial patches would make deletion
   * inexpressible in prose, which is the one thing prose is good at.
   */
  sequence: z.array(campaignEditStepSchema).nullable(),
  success: z
    .object({
      kind: z.enum(CAMPAIGN_EDIT_SUCCESS_KINDS),
      /** Only meaningful when `kind` is `outcome`. */
      anyOf: z.array(z.string()).nullable(),
    })
    .nullable(),
  stopOn: z.array(z.string()).nullable(),
  maxTotalAttempts: z.number().int().min(1).max(50).nullable(),
  /** `null` inside the object means "never give up"; the object itself null means unchanged. */
  expiresAfterDays: z.object({ days: z.number().int().min(1).nullable() }).nullable(),
  window: z
    .object({
      days: z.array(z.enum(WINDOW_DAYS)),
      /*
        The same `HH:mm` the spec demands, restated here on purpose. Left as a
        bare string, a model answering "9am" passed this schema and then failed
        `campaignSpecSchema.parse` at the very end — failing the WHOLE compile,
        including every unrelated edit in the same block of text, with a message
        naming a field the author never typed. Constrained here, structured
        output rejects it and re-prompts the model automatically.
      */
      start: timeOfDayString,
      end: timeOfDayString,
      useLocationHours: z.boolean(),
    })
    .nullable(),
});
export type CampaignEdit = z.infer<typeof campaignEditSchema>;

/** Display name → id, for resolving what the model named back to what the spec stores. */
export type NameResolver = (field: AudienceFilterField, name: string) => string | undefined;

export interface ApplyEditResult {
  spec: CampaignSpec;
  /**
   * What could not be applied, in the author's words.
   *
   * Never thrown and never silent. A model naming a location that does not
   * exist is the expected failure of this feature, and dropping it quietly
   * would produce a campaign that calls a different audience than the sentence
   * on screen claims.
   */
  warnings: string[];
}

const VALID_OUTCOMES: ReadonlySet<string> = new Set(OUTBOUND_CALL_OUTCOME_VALUES);

/** Keeps only outcomes this build knows, warning about the rest. */
function cleanOutcomes(values: readonly string[], where: string, warnings: string[]): string[] {
  const kept = values.filter((v) => VALID_OUTCOMES.has(v));
  for (const dropped of values.filter((v) => !VALID_OUTCOMES.has(v))) {
    warnings.push(`Ignored "${dropped}" in ${where} — that is not an outcome a call can have.`);
  }
  return kept;
}

function applyAudience(
  current: Audience,
  edit: NonNullable<CampaignEdit['audience']>,
  resolve: NameResolver,
  warnings: string[]
): Audience {
  const type = edit.type ?? current.type;

  // A different condition takes different parameters, so the old ones are not
  // merely stale — a key that happens to collide would mean something else
  // entirely. Start from the new condition's own defaults.
  const base = type === current.type ? current : defaultAudience(type);
  const params: Record<string, number | boolean> = {
    ...(base.params as Record<string, number | boolean>),
  };

  if (edit.params) {
    const known = new Set(audienceParamFields(type).map((f) => f.key));
    for (const param of edit.params) {
      if (!known.has(param.key)) {
        warnings.push(`Ignored "${param.key}" — that setting does not apply to this audience.`);
        continue;
      }
      const value = param.numberValue ?? param.booleanValue;
      if (value == null) continue;
      params[param.key] = value;
    }
  }

  let filters = base.filters;
  if (edit.filters) {
    filters = [];
    for (const criterion of edit.filters) {
      const values: string[] = [];
      for (const name of criterion.names) {
        const id = resolve(criterion.field, name);
        if (id) values.push(id);
        else
          warnings.push(`Could not find "${name}" — that ${criterion.field} filter was skipped.`);
      }
      // An empty criterion fails `min(1)` and would compile to `IN ()`, matching
      // nobody. Dropping it narrows by one dimension less, which is at least
      // what the unresolved-name warning already says happened.
      if (values.length) filters.push({ field: criterion.field, values });
    }
  }

  const parsed = audienceSchema.safeParse({ type, params, filters });
  if (parsed.success) return parsed.data;

  warnings.push('Could not apply the audience change — kept the one already set.');
  return current;
}

/**
 * The next `step-N` no step in this sequence has ever used.
 *
 * `taken` accumulates across the whole apply, so it holds both the ids the
 * campaign already had and the ones minted earlier in this same pass. Deriving
 * an id from the entry's index instead — which is what this replaced — collides
 * the moment a sequence already contains a step whose id came from that index:
 * ['step-1', 'step-new-2'] plus a new step inserted at index 1 produced a second
 * 'step-new-2', and `campaignSpecSchema` has no duplicate-id check to catch it.
 * Two steps sharing an id makes every attempt row referencing it ambiguous.
 */
function mintStepId(taken: Set<string>): string {
  let n = taken.size + 1;
  while (taken.has(`step-${n}`)) n++;
  const id = `step-${n}`;
  taken.add(id);
  return id;
}

function applyStep(
  entry: CampaignEdit['sequence'] extends Array<infer T> | null ? T : never,
  existing: SequenceStep | undefined,
  id: string,
  warnings: string[]
): SequenceStep {
  /*
    ── Fix 2: an SMS step cannot be *introduced* here ──
    The editor renders the Text channel as disabled on purpose: an SMS step
    resolves on silence, which needs conversation storage that does not exist
    yet, so publishing one produces a step the driver reaches and never
    resolves. Prose has no such guard, and "text them instead" is a natural
    thing to write — so the restriction lives here too.

    An existing SMS step keeps its channel. Forcing those to voice would be the
    opposite failure: silently converting a step the campaign already has,
    because the model had no way to say "leave it as it was".
  */
  const channel = entry.channel === 'sms' && existing?.channel !== 'sms' ? 'voice' : entry.channel;
  if (channel !== entry.channel) {
    warnings.push(`"${entry.label}" stays a call — text steps are not available yet.`);
  }

  /*
    `null` means the text did not say, which is an instruction to keep what the
    step had — and `false` for a step that did not exist before. Reading null as
    false would turn every silence into a deliberate "off", which is precisely
    the bug these fields are nullable to prevent.
  */
  const leaveMessage =
    entry.leaveMessage ?? (existing ? existing.onMachine.kind === 'leave_message' : false);

  // Preserve the slot a message was already configured with. Re-deriving it as
  // `default` would silently repoint every voicemail on a step whose sentence
  // said nothing about which message it leaves.
  const onMachine: SequenceStep['onMachine'] = leaveMessage
    ? {
        kind: 'leave_message',
        slot: existing?.onMachine.kind === 'leave_message' ? existing.onMachine.slot : 'default',
      }
    : { kind: 'hang_up' };

  const rotate = entry.rotateTimeOfDay ?? existing?.timeOfDay === 'rotate';
  // A NEW step honours a requested callback unless the text said otherwise —
  // the schema's own default since ADR-318. An existing step keeps what it had.
  const honourCallback =
    entry.honourRequestedCallback ??
    (existing ? existing.onCallbackRequested === 'use_requested_time' : true);

  return {
    id,
    label: entry.label.slice(0, 80),
    channel,
    action: channel === 'sms' ? { kind: 'sms' } : { kind: 'call' },
    // Corrected by `normaliseTriggers` for position; the first step's wait is
    // meaningless and the schema rejects it.
    trigger: { kind: 'after_previous', minutes: entry.waitBeforeMinutes },
    repeat: { times: entry.times, spacingMinutes: entry.spacingMinutes },
    // Not expressible in prose, and channel-determined. An SMS step resolving
    // on call-end would never resolve at all.
    resolve:
      existing && existing.channel === channel
        ? existing.resolve
        : channel === 'sms'
          ? { kind: 'on_quiet', quietForMinutes: 1440 }
          : { kind: 'on_call_end' },
    continueOn: cleanOutcomes(entry.continueOn, `"${entry.label}"`, warnings),
    // Only ever narrows the campaign window, and no sentence states it.
    window: existing?.window ?? null,
    onMachine,
    timeOfDay: rotate ? 'rotate' : 'fixed',
    onCallbackRequested: honourCallback ? 'use_requested_time' : 'use_standard_wait',
    // No sentence states any of these (ADR-318), so an edit carries them across
    // untouched and a new step gets the schema's defaults. Dropping the pinned
    // workflow on a round trip would leave a voice step unpublishable.
    workflow: existing?.workflow ?? null,
    agentId: existing?.agentId ?? null,
    afterLastTry: existing?.afterLastTry ?? 'next',
    // A NEW step is timed the way the editor times one it adds (G): Automatic.
    // Null is kept only for a step that already had it, published before
    // `timing` existed, whose calls must not quietly move.
    timing: existing ? existing.timing : { mode: 'auto' },
  };
}

/**
 * Fix triggers for the positions steps now hold.
 *
 * Only the first step may be started by qualification — `campaignSpecSchema`
 * rejects anything else — so a compiled sequence has to be corrected for
 * position rather than trusted. Duplicated from the editor's own
 * `normaliseTriggers` deliberately: this one runs on the server, where the
 * editor's module does not exist.
 */
function normalisePositions(sequence: SequenceStep[]): SequenceStep[] {
  return sequence.map((step, index) =>
    index === 0
      ? { ...step, trigger: { kind: 'qualifies' as const } }
      : step.trigger.kind === 'after_previous'
        ? step
        : { ...step, trigger: { kind: 'after_previous' as const, minutes: 1440 } }
  );
}

/**
 * Merge a compiled edit onto the campaign it was written against.
 *
 * Pure: the model call happens elsewhere, so every merge rule here is testable
 * without one. Throws only if the result cannot be a campaign at all, which the
 * caller reports as a failed compile rather than a partial apply — a spec that
 * parses halfway is the one outcome worse than not applying the edit.
 */
export function applyCampaignEdit(
  current: CampaignSpec,
  edit: CampaignEdit,
  resolve: NameResolver
): ApplyEditResult {
  const warnings: string[] = [];

  const audience = edit.audience
    ? applyAudience(current.audience, edit.audience, resolve, warnings)
    : current.audience;

  let sequence = current.sequence;
  if (edit.sequence) {
    if (edit.sequence.length === 0) {
      warnings.push('Kept the existing steps — a campaign with no steps cannot run.');
    } else {
      const byId = new Map(current.sequence.map((s) => [s.id, s]));
      const used = new Set<string>();
      // Seeded with every id the campaign already holds, so a newly minted id
      // can never land on one of them — including ids of steps this edit drops,
      // which attempts in the database still reference.
      const taken = new Set(current.sequence.map((s) => s.id));

      sequence = normalisePositions(
        edit.sequence.map((entry) => {
          // An id claimed twice would fuse two steps' histories onto one.
          const existing = entry.id && !used.has(entry.id) ? byId.get(entry.id) : undefined;
          if (existing) used.add(existing.id);
          return applyStep(entry, existing, existing?.id ?? mintStepId(taken), warnings);
        })
      );

      for (const step of current.sequence) {
        if (!used.has(step.id)) {
          warnings.push(`Removed the step "${step.label ?? step.id}".`);
        }
      }
    }
  }

  const success: CampaignSpec['success'] = edit.success
    ? edit.success.kind === 'outcome'
      ? {
          kind: 'outcome',
          anyOf: cleanOutcomes(edit.success.anyOf ?? [], 'what counts as success', warnings),
        }
      : { kind: edit.success.kind }
    : current.success;

  // An `outcome` success with nothing in it fails `min(1)`. The author asked
  // for a success condition, so saying it was kept is more useful than a
  // validation error from a screen that looked fine.
  const safeSuccess =
    success.kind === 'outcome' && success.anyOf.length === 0
      ? (warnings.push('Kept the previous success rule — no valid outcome was named.'),
        current.success)
      : success;

  const next = {
    ...current,
    audience,
    sequence,
    success: safeSuccess,
    stopOn: edit.stopOn
      ? cleanOutcomes(edit.stopOn, 'what stops the run', warnings)
      : current.stopOn,
    maxTotalAttempts: edit.maxTotalAttempts ?? current.maxTotalAttempts,
    expiresAfterDays: edit.expiresAfterDays ? edit.expiresAfterDays.days : current.expiresAfterDays,
    window: edit.window
      ? {
          days: edit.window.days.length ? edit.window.days : current.window.days,
          start: edit.window.start,
          end: edit.window.end,
          useLocationHours: edit.window.useLocationHours,
        }
      : current.window,
  };

  return { spec: campaignSpecSchema.parse(next), warnings };
}
